import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Textarea } from "../../../../components/ui/textarea";
import { Input } from "../../../../components/ui/input";
import {
  TemplateFieldResponse,
  FieldState,
  getFieldTypeLabel,
  ComputedTableValue,
} from "../../../../models/workPaperTemplate";
import { ControlAttribute } from "../../../../models/audit/control";
import { sourceRowSchema } from "../../../../models/audit/auditSession";
import { AlertCircle, Check, X, Pencil } from "lucide-react";
import { z } from "zod";

type SourceRow = z.infer<typeof sourceRowSchema>;

interface FieldValueCardProps {
  field: TemplateFieldResponse;
  fieldState: FieldState | undefined;
  onUpdate?: (fieldId: string, newValue: string) => Promise<void>;
  updating?: boolean;
  attributeLookup: Map<string, ControlAttribute>;
  sampleLookup: Map<string, SourceRow>;
  onUpdateAttributeDescription?: (
    attributeId: string,
    description: string,
  ) => Promise<void>;
  updatingAttributeId?: string | null;
  onUpdateComputedTableValue?: (
    fieldId: string,
    tableValue: ComputedTableValue,
  ) => Promise<void>;
  updatingComputedTableFieldId?: string | null;
}

export const FieldValueCard = ({
  field,
  fieldState,
  onUpdate,
  updating = false,
  attributeLookup,
  sampleLookup,
  onUpdateAttributeDescription,
  updatingAttributeId,
  onUpdateComputedTableValue,
  updatingComputedTableFieldId,
}: FieldValueCardProps) => {
  const fieldTitle = field.name;
  const fieldTypeLabel = getFieldTypeLabel(field.fieldType);

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [editingAttributeId, setEditingAttributeId] = useState<string | null>(
    null,
  );
  const [editAttributeDescription, setEditAttributeDescription] = useState("");
  const [editingTableCell, setEditingTableCell] = useState<{
    rowIndex: number;
    columnId: string;
  } | null>(null);
  const [editTableCellValue, setEditTableCellValue] = useState("");

  // Determine if this field can be edited (only dynamic fields for now)
  const canEdit = field.fieldType === "dynamic" && onUpdate;

  const getCurrentValue = (): string | null => {
    if (!fieldState) return null;

    switch (field.fieldType) {
      case "static":
        return fieldState.staticValue;
      case "dynamic":
        return fieldState.computedValue;
      default:
        return null;
    }
  };

  const handleEditClick = () => {
    const currentValue = getCurrentValue();
    setEditValue(currentValue || "");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue("");
  };

  const handleSaveEdit = async () => {
    if (!onUpdate) return;

    try {
      await onUpdate(field.id, editValue);
      setIsEditing(false);
      setEditValue("");
    } catch (error) {
      console.error("Failed to update field value:", error);
    }
  };

  const handleEditAttributeDescription = (
    attributeId: string,
    currentDescription: string,
  ) => {
    setEditingAttributeId(attributeId);
    setEditAttributeDescription(currentDescription || "");
  };

  const handleCancelAttributeEdit = () => {
    setEditingAttributeId(null);
    setEditAttributeDescription("");
  };

  const handleSaveAttributeDescription = async (attributeId: string) => {
    if (!onUpdateAttributeDescription) return;

    try {
      await onUpdateAttributeDescription(attributeId, editAttributeDescription);
      setEditingAttributeId(null);
      setEditAttributeDescription("");
    } catch (error) {
      console.error("Failed to update attribute description:", error);
    }
  };

  const handleEditTableCell = (
    rowIndex: number,
    columnId: string,
    currentValue: string | number | boolean | null,
  ) => {
    setEditingTableCell({ rowIndex, columnId });
    setEditTableCellValue(
      currentValue === null || currentValue === undefined
        ? ""
        : String(currentValue),
    );
  };

  const handleCancelTableCellEdit = () => {
    setEditingTableCell(null);
    setEditTableCellValue("");
  };

  const handleSaveTableCellValue = async () => {
    if (
      !onUpdateComputedTableValue ||
      !fieldState?.computedTableValue ||
      !editingTableCell
    )
      return;

    const { rowIndex, columnId } = editingTableCell;

    // Get the column definition to determine the data type
    const columnDef = field.computedTableField?.columns.find(
      (col) => col.id === columnId,
    );
    const dataType = columnDef?.dataType ?? "string";

    // Parse the value based on the column's data type
    let parsedValue: string | number | boolean | null = editTableCellValue;
    if (editTableCellValue === "") {
      parsedValue = null;
    } else if (dataType === "number") {
      const numValue = parseFloat(editTableCellValue);
      parsedValue = isNaN(numValue) ? null : numValue;
    } else if (dataType === "boolean") {
      parsedValue =
        editTableCellValue.toLowerCase() === "true" ||
        editTableCellValue === "1" ||
        editTableCellValue.toLowerCase() === "yes";
    }

    // Create a new table value with the updated cell
    const updatedRows = fieldState.computedTableValue.rows.map((row, idx) => {
      if (idx === rowIndex) {
        return {
          ...row,
          values: {
            ...row.values,
            [columnId]: parsedValue,
          },
        };
      }
      return row;
    });

    try {
      await onUpdateComputedTableValue(field.id, { rows: updatedRows });
      setEditingTableCell(null);
      setEditTableCellValue("");
    } catch (error) {
      console.error("Failed to update table cell value:", error);
    }
  };

  const renderValue = () => {
    // Special handling for detail testing table and citation asset sheet
    // These are populated during download, not in the UI
    if (
      field.fieldType === "detail_testing_table" ||
      field.fieldType === "citation_asset_sheet"
    ) {
      return (
        <div className="text-sm text-muted-foreground">
          This field will be populated when the workpaper is downloaded.
        </div>
      );
    }

    if (!fieldState) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">No value generated yet</span>
        </div>
      );
    }

    // Handle different field types
    switch (field.fieldType) {
      case "static":
        if (fieldState.staticValue === null) {
          return (
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">No value available</span>
            </div>
          );
        }
        return (
          <div className="text-sm whitespace-pre-wrap">
            {fieldState.staticValue}
          </div>
        );

      case "dynamic":
        if (fieldState.computedValue === null) {
          return (
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">Not yet generated</span>
            </div>
          );
        }

        if (isEditing) {
          return (
            <div className="space-y-3">
              <Textarea
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="min-h-[120px] font-mono text-sm"
                placeholder="Enter field value..."
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveEdit}
                  disabled={updating}
                  loading={updating}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={updating}
                >
                  Cancel
                </Button>
              </div>
            </div>
          );
        }

        return (
          <div className="text-sm whitespace-pre-wrap">
            {fieldState.computedValue}
          </div>
        );

      case "attribute_summary_table": {
        if (fieldState.attributeTableValue === null) {
          return (
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">No attribute data available</span>
            </div>
          );
        }

        // Check if evidence summary is configured (separate column mode)
        const showEvidenceSummaryColumn =
          field.attributeSummaryTable?.evidenceSummaryColumn != null;
        // Check if concat mode is enabled (evidence summary appended to description)
        const concatEvidenceMode =
          field.attributeSummaryTable?.concatEvidenceWithDescription === true;

        return (
          <div className="border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Attribute
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Outcome
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      {concatEvidenceMode
                        ? "Description + Evidence"
                        : "Description"}
                    </th>
                    {showEvidenceSummaryColumn && (
                      <th className="px-3 py-2 text-left font-medium text-gray-700">
                        Evidence Summary
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {[...fieldState.attributeTableValue.rows]
                    .sort((a, b) => {
                      const attrA = attributeLookup.get(a.attributeId);
                      const attrB = attributeLookup.get(b.attributeId);
                      const orderA = attrA?.attributeOrder ?? 999999;
                      const orderB = attrB?.attributeOrder ?? 999999;
                      return orderA - orderB;
                    })
                    .map((row) => {
                      const attribute = attributeLookup.get(row.attributeId);
                      const displayName = attribute
                        ? `${attribute.reference} - ${attribute.statement}`
                        : row.attributeId;
                      return (
                        <tr
                          key={row.attributeId}
                          className="border-b last:border-0"
                        >
                          <td className="px-3 py-2">{displayName}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              {row.attributeOutcome === "pass" && (
                                <Check className="h-4 w-4 text-green-600" />
                              )}
                              {row.attributeOutcome === "fail" && (
                                <X className="h-4 w-4 text-red-600" />
                              )}
                              <span className="capitalize">
                                {row.attributeOutcome}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            {editingAttributeId === row.attributeId ? (
                              <div className="space-y-2">
                                <Textarea
                                  value={editAttributeDescription}
                                  onChange={(e) =>
                                    setEditAttributeDescription(e.target.value)
                                  }
                                  className="min-h-[80px] text-sm"
                                  placeholder="Enter description..."
                                  disabled={
                                    updatingAttributeId === row.attributeId
                                  }
                                />
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() =>
                                      handleSaveAttributeDescription(
                                        row.attributeId,
                                      )
                                    }
                                    disabled={
                                      updatingAttributeId === row.attributeId
                                    }
                                    loading={
                                      updatingAttributeId === row.attributeId
                                    }
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleCancelAttributeEdit}
                                    disabled={
                                      updatingAttributeId === row.attributeId
                                    }
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground flex-1">
                                  {concatEvidenceMode &&
                                  row.attributeOutcomeDescription &&
                                  row.evidenceSummary
                                    ? `${row.attributeOutcomeDescription}\n\n${row.evidenceSummary}`
                                    : row.attributeOutcomeDescription || "-"}
                                </span>
                                {onUpdateAttributeDescription && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      handleEditAttributeDescription(
                                        row.attributeId,
                                        row.attributeOutcomeDescription || "",
                                      )
                                    }
                                    className="h-6 w-6 p-0"
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                          {showEvidenceSummaryColumn && (
                            <td className="px-3 py-2">
                              <span className="text-muted-foreground">
                                {row.evidenceSummary || "-"}
                              </span>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case "sample_summary_table":
        if (fieldState.sampleTableValue === null) {
          return (
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">No sample data available</span>
            </div>
          );
        }
        return (
          <div className="border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Sample
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Attribute Results
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...fieldState.sampleTableValue.rows]
                    .sort((a, b) => {
                      const sampleA = sampleLookup.get(a.sampleId);
                      const sampleB = sampleLookup.get(b.sampleId);
                      const numA = sampleA?.sampleNumber ?? 999999;
                      const numB = sampleB?.sampleNumber ?? 999999;
                      return numA - numB;
                    })
                    .map((row) => {
                      const sourceRow = sampleLookup.get(row.sampleId);
                      const sampleDisplayName =
                        sourceRow && sourceRow.sampleNumber !== null
                          ? `Sample ${sourceRow.sampleNumber}`
                          : row.sampleId;
                      return (
                        <tr
                          key={row.sampleId}
                          className="border-b last:border-0"
                        >
                          <td className="px-3 py-2">{sampleDisplayName}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-2">
                              {[...row.attributeResults]
                                .sort((a, b) => {
                                  const attrA = attributeLookup.get(
                                    a.attributeId,
                                  );
                                  const attrB = attributeLookup.get(
                                    b.attributeId,
                                  );
                                  const orderA =
                                    attrA?.attributeOrder ?? 999999;
                                  const orderB =
                                    attrB?.attributeOrder ?? 999999;
                                  return orderA - orderB;
                                })
                                .map((result) => {
                                  const attribute = attributeLookup.get(
                                    result.attributeId,
                                  );
                                  const attributeLabel = attribute
                                    ? attribute.reference
                                    : result.attributeId;
                                  return (
                                    <span
                                      key={result.attributeId}
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs"
                                    >
                                      <span className="font-medium">
                                        {attributeLabel}:
                                      </span>
                                      <span>{result.outcome}</span>
                                    </span>
                                  );
                                })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        );

      case "computed_table": {
        if (!fieldState.computedTableValue || !field.computedTableField) {
          return (
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">No table data available</span>
            </div>
          );
        }
        // Sort columns by columnOrder
        const sortedColumns = [...field.computedTableField.columns].sort(
          (a, b) => a.columnOrder - b.columnOrder,
        );

        if (fieldState.computedTableValue.rows.length === 0) {
          return (
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">
                Table is empty (no rows generated)
              </span>
            </div>
          );
        }

        const isUpdating = updatingComputedTableFieldId === field.id;

        return (
          <div className="border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    {sortedColumns.map((col) => (
                      <th
                        key={col.id}
                        className="px-3 py-2 text-left font-medium text-gray-700"
                      >
                        {col.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fieldState.computedTableValue.rows.map((row, rowIndex) => (
                    <tr key={rowIndex} className="border-b last:border-0 group">
                      {sortedColumns.map((col) => {
                        const value = row.values[col.id];
                        const isEditingThisCell =
                          editingTableCell?.rowIndex === rowIndex &&
                          editingTableCell?.columnId === col.id;

                        if (isEditingThisCell) {
                          return (
                            <td key={col.id} className="px-3 py-2">
                              <div className="space-y-2">
                                <Input
                                  value={editTableCellValue}
                                  onChange={(e) =>
                                    setEditTableCellValue(e.target.value)
                                  }
                                  className="h-8 text-sm"
                                  placeholder="Enter value..."
                                  disabled={isUpdating}
                                />
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    onClick={handleSaveTableCellValue}
                                    disabled={isUpdating}
                                    loading={isUpdating}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleCancelTableCellEdit}
                                    disabled={isUpdating}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </td>
                          );
                        }

                        let displayValue: React.ReactNode;
                        if (value === null || value === undefined) {
                          displayValue = (
                            <span className="text-muted-foreground">-</span>
                          );
                        } else if (typeof value === "boolean") {
                          displayValue = value ? "Yes" : "No";
                        } else if (typeof value === "number") {
                          displayValue = value.toLocaleString();
                        } else {
                          displayValue = String(value);
                        }

                        return (
                          <td key={col.id} className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="flex-1">{displayValue}</span>
                              {onUpdateComputedTableValue && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    handleEditTableCell(rowIndex, col.id, value)
                                  }
                                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      default:
        return (
          <div className="text-sm text-muted-foreground">
            Unknown field type
          </div>
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{fieldTitle}</CardTitle>
          <div className="flex items-center gap-2">
            {canEdit && !isEditing && fieldState?.computedValue && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleEditClick}
                disabled={updating}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            <Badge variant="outline">{fieldTypeLabel}</Badge>
          </div>
        </div>
        <CardDescription>
          {field.fieldType === "static" && field.staticField && (
            <span>Source: {field.staticField.sourceSelector}</span>
          )}
          {field.fieldType === "dynamic" && field.computedField && (
            <span>AI Generated: {field.computedField.instructions}</span>
          )}
          {field.fieldType === "attribute_summary_table" &&
            field.attributeSummaryTable && (
              <span>
                Attribute Table starting at row{" "}
                {field.attributeSummaryTable.startRow}
              </span>
            )}
          {field.fieldType === "sample_summary_table" &&
            field.sampleSummaryTable && (
              <span>
                Sample Table starting at row {field.sampleSummaryTable.startRow}
              </span>
            )}
          {field.fieldType === "detail_testing_table" &&
            field.detailTestingTable && (
              <span>
                Testing tables per procedure (Sheet prefix:{" "}
                {field.detailTestingTable.sheetNamePrefix})
              </span>
            )}
          {field.fieldType === "citation_asset_sheet" &&
            field.citationAssetSheet && (
              <span>
                Citation asset sheets per procedure (Sheet prefix:{" "}
                {field.citationAssetSheet.sheetNamePrefix})
              </span>
            )}
          {field.fieldType === "computed_table" && field.computedTableField && (
            <span>
              AI-Generated Table: {field.computedTableField.instructions}
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>{renderValue()}</CardContent>
    </Card>
  );
};
